import { ForcedSubject, MongoQuery, PureAbility } from '@casl/ability';

export const ACTIONS = {
    READ: 'read',
    CREATE: 'create',
    MANAGE: 'manage',
    UPDATE: 'update',
    INVITE: 'invite',
    COMMENT: 'comment',
} as const;

export const SUBJECTS = {
    // Workspace
    ALL: 'all',
    WORKSPACE: 'Workspace',
    USER: 'User',
    MEMBER: 'Member',
    // Workspace Resources
    FOLDER: 'Folder',
    IMPORTED_ENTITY: 'ImportedEntity',
    INVESTMENT: 'Investment',
    MEMO: 'Memo',
    PIPELINE: 'Pipeline',
    DASHBOARD: 'Dashboard',
    LIST: 'List',
    LIFECYCLE: 'Lifecycle',
    TEAM: 'Team',
    // Nested
    REPORT: 'Report',
    LISTVIEW: 'ListView',
} as const;

export type WorkspaceActions = 'read' | 'manage' | 'invite' | 'update' | 'create';
export type WorkspaceSubjects =
    | 'all'
    | 'Workspace'
    | 'User'
    | 'Member'
    | 'Folder'
    | 'ImportedEntity'
    | 'Investment'
    | 'Memo'
    | 'Pipeline'
    | 'Dashboard'
    | 'List'
    | 'Lifecycle'
    | 'Team';

export type ResourceActions = 'read' | 'manage' | 'comment' | 'invite' | 'update';
export type ResourceSubjects =
    | 'all'
    | 'Folder'
    | 'ImportedEntity'
    | 'Investment'
    | 'Memo'
    | 'Pipeline'
    | 'Dashboard'
    | 'List'
    | 'Lifecycle';

export type NestedResourceActions = 'read' | 'manage' | 'comment' | 'invite' | 'update';
export type NestedResourceSubjects = 'all' | 'Report' | 'ListView';

export type AppActions = WorkspaceActions | ResourceActions | NestedResourceActions;
export type AppSubjects = WorkspaceSubjects | ResourceSubjects | NestedResourceSubjects;
export type AppSubject = AppSubjects | ({ id: string } & ForcedSubject<AppSubjects>);

export type AppAbility = PureAbility<[AppActions, AppSubject], MongoQuery>;

export type PermissionTuple = readonly [AppActions, AppSubjects];

export const PERMISSIONS = {
    WORKSPACE: {
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.ALL] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.WORKSPACE] as const,
        INVITE_USER: [ACTIONS.INVITE, SUBJECTS.USER] as const,
        READ: [ACTIONS.READ, SUBJECTS.WORKSPACE] as const,
    },
    FOLDER: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.FOLDER] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.FOLDER] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.FOLDER] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.FOLDER] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.FOLDER] as const,
        READ: [ACTIONS.READ, SUBJECTS.FOLDER] as const,
    },
    IMPORTED_ENTITY: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.IMPORTED_ENTITY] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.IMPORTED_ENTITY] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.IMPORTED_ENTITY] as const,
        READ: [ACTIONS.READ, SUBJECTS.IMPORTED_ENTITY] as const,
    },
    INVESTMENT: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.INVESTMENT] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.INVESTMENT] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.INVESTMENT] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.INVESTMENT] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.INVESTMENT] as const,
        READ: [ACTIONS.READ, SUBJECTS.INVESTMENT] as const,
    },
    MEMO: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.MEMO] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.MEMO] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.MEMO] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.MEMO] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.MEMO] as const,
        READ: [ACTIONS.READ, SUBJECTS.MEMO] as const,
    },
    PIPELINE: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.PIPELINE] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.PIPELINE] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.PIPELINE] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.PIPELINE] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.PIPELINE] as const,
        READ: [ACTIONS.READ, SUBJECTS.PIPELINE] as const,
    },
    DASHBOARD: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.DASHBOARD] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.DASHBOARD] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.DASHBOARD] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.DASHBOARD] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.DASHBOARD] as const,
        READ: [ACTIONS.READ, SUBJECTS.DASHBOARD] as const,
    },
    REPORT: {
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.REPORT] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.REPORT] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.REPORT] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.REPORT] as const,
        READ: [ACTIONS.READ, SUBJECTS.REPORT] as const,
    },
    LIST: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.LIST] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.LIST] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.LIST] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.LIST] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.LIST] as const,
        READ: [ACTIONS.READ, SUBJECTS.LIST] as const,
    },
    LIFECYCLE: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.LIFECYCLE] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.LIFECYCLE] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.LIFECYCLE] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.LIFECYCLE] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.LIFECYCLE] as const,
        READ: [ACTIONS.READ, SUBJECTS.LIFECYCLE] as const,
    },
    TEAM: {
        CREATE: [ACTIONS.CREATE, SUBJECTS.TEAM] as const,
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.TEAM] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.TEAM] as const,
        READ: [ACTIONS.READ, SUBJECTS.TEAM] as const,
    },
    LISTVIEW: {
        MANAGE: [ACTIONS.MANAGE, SUBJECTS.LISTVIEW] as const,
        INVITE: [ACTIONS.INVITE, SUBJECTS.LISTVIEW] as const,
        UPDATE: [ACTIONS.UPDATE, SUBJECTS.LISTVIEW] as const,
        COMMENT: [ACTIONS.COMMENT, SUBJECTS.LISTVIEW] as const,
        READ: [ACTIONS.READ, SUBJECTS.LISTVIEW] as const,
    },
} as const satisfies Record<string, Record<string, PermissionTuple>>;

export * from './resourceActivityExport';
